/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box } from '@mui/material';
import React from 'react';
import { useDispatch } from 'react-redux';
import type { Credentials } from '@opengeoweb/api';
import { refreshAccessTokenAndSetAuthContext } from '@opengeoweb/api';
import type { AuthenticationConfig } from '@opengeoweb/authentication';
import { AlertBanner } from '@opengeoweb/shared';
import { snackbarActions, snackbarTypes } from '@opengeoweb/snackbar';
import { useGeowebTranslation } from '../../i18n';

export const useOnline = (): {
  isOnline: boolean | string;
  userTrigger: () => void;
} => {
  const mounted = React.useRef(true);
  const [isOnline, setIsOnline] = React.useState<boolean | string>(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );

  const ping = React.useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 3000);
      const res = await fetch('/assets/config.json', {
        cache: 'no-store',
        signal: controller.signal,
      });
      clearTimeout(timeout);
      if (!mounted.current) {
        return;
      }
      if (res?.ok) {
        setIsOnline(true);
      } else {
        setIsOnline(false);
      }
    } catch {
      if (!mounted.current) {
        return;
      }
      setIsOnline(false);
    }
  }, []);

  React.useEffect(() => {
    mounted.current = true;

    window.addEventListener('online', ping);
    window.addEventListener('offline', ping);

    // initial ping
    void ping();

    // Check every 60 seconds whether we are still online
    const id = setInterval(ping, 60_000);

    return (): void => {
      mounted.current = false;
      clearInterval(id);
      window.removeEventListener('online', ping);
      window.removeEventListener('offline', ping);
    };
  }, [ping]);

  const userTrigger = (): void => {
    setIsOnline('Loading');
    void ping();
  };

  return { isOnline, userTrigger };
};

interface ConnectionIssueBannerProps {
  auth?: Credentials;
  onSetAuth?: (cred: Credentials) => void;
  authConfig?: AuthenticationConfig;
}

export const ConnectionIssueBanner: React.FC<ConnectionIssueBannerProps> = ({
  auth,
  onSetAuth,
  authConfig,
}: ConnectionIssueBannerProps) => {
  const { t } = useGeowebTranslation();
  const dispatch = useDispatch();

  const showSnackbar = React.useCallback(
    (message: snackbarTypes.SnackbarMessage) => {
      dispatch(snackbarActions.openSnackbar(message));
    },
    [dispatch],
  );

  const { isOnline, userTrigger } = useOnline();

  const hasError = auth?.has_connection_issue === true;
  if (!hasError && isOnline) {
    // There are no connection issues, so nothing to show.
    return null;
  }
  const checkConnection = async (): Promise<void> => {
    // Connection issue, try and see if we're back online
    if (!isOnline) {
      userTrigger();
      return;
    }
    // Auth issue, try and refresh the access token
    onSetAuth!({ ...(auth as Credentials), has_connection_issue: false });
    await refreshAccessTokenAndSetAuthContext({
      auth,
      onSetAuth: (cred: Credentials): void => {
        if (cred.has_connection_issue !== true) {
          showSnackbar({
            type: snackbarTypes.SnackbarMessageType.TRANSLATABLE_MESSAGE,
            key: 'geoweb-connection-restored',
          });
        }
        onSetAuth!(cred);
      },
      config: {
        baseURL: '',
        authTokenURL: authConfig!.GW_AUTH_TOKEN_URL,
        appURL: authConfig!.GW_APP_URL,
        authClientId: authConfig!.GW_AUTH_CLIENT_ID,
      },
    });
  };

  return (
    <div style={{ position: 'absolute', left: '20px', top: '50px' }}>
      <Box
        sx={{
          whiteSpace: 'pre-wrap',
          width: 'inherit',
          height: 'inherit',
          backgroundColor: 'geowebColors.background.surface',
        }}
      >
        <AlertBanner
          actionButtonProps={{
            title: t('geoweb-try-again'),
            onClick: checkConnection,
          }}
          severity="error"
          title={
            hasError
              ? t('geoweb-connection-auth-lost')
              : t('geoweb-connection-lost')
          }
        />
      </Box>
    </div>
  );
};
